import React, { useEffect, useState } from 'react';
import {
  RequestsCard,
  RequestsCardNoRequestsText,
  RequestsCardType,
  StringDateRange,
  initTimeOffCommonUi,
  supportedLocaleFromString,
  SupportedLocales,
} from '@amzn/time-off-common-ui';
import { DateTime } from 'luxon';
import { QueryClient, QueryClientProvider } from 'react-query';
import { WidgetView, WidgetViewProps } from 'src/models/WidgetView';
import { getTORWidgetDateRange, isDateInPast, NO_OF_REQUESTS_TO_DISPLAY, query } from 'src/constants/TimeOffCommonUi';
import { OutputTimeOffRequest } from '@amzn/taa-rtk-query-client';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { KatalLoggerClient } from 'src/logger/KatalLoggerClient';
import { TOR_ALL_REDIRECT_LINK, TOR_REQUEST_REDIRECT_LINK } from 'src/constants/common';
import { getTAAComponentConfig } from 'src/components/Widgets/WidgetUtils';
import { DEFAULT_LOCALE } from 'src/constants';

export interface TimeoffRequestsViewProps extends WidgetViewProps {}

const TimeoffRequestsView: WidgetView = ({ widgetInfo, employeeInfo }: TimeoffRequestsViewProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const katalLogger = new KatalLoggerClient();
  const taaConfigs = getTAAComponentConfig(employeeInfo);
  useEffect(() => {
    const initializeTimeOff = async () => {
      const supportedLocale =
        supportedLocaleFromString(taaConfigs?.associateInfo?.locale || DEFAULT_LOCALE) || SupportedLocales.EN_US;
      try {
        await initTimeOffCommonUi({
          locale: supportedLocale,
          queryConfig: {
            taapi: query,
          },
        });
        setIsInitialized(true);
      } catch (error) {
        await katalLogger.error('Failed to initialize TimeOff UI:', new Error(String(error)));
        setIsInitialized(true);
      }
    };

    initializeTimeOff();
  }, []);

  const redirectViewAllRequests = () => {
    window.open(TOR_ALL_REDIRECT_LINK);
  };

  const redirectViewRequest = (timeoffRequest: OutputTimeOffRequest) => {
    window.open(TOR_REQUEST_REDIRECT_LINK(timeoffRequest.timeOffRequestId));
  };

  const isRequestDateInPast = isDateInPast(widgetInfo.widgetInputs.requestDate, 'yyyy-MM-dd', employeeInfo.timeZoneId);
  const torDateRange: StringDateRange = getTORWidgetDateRange(
    widgetInfo.widgetInputs.requestDate,
    'yyyy-MM-dd',
    employeeInfo.timeZoneId,
  );

  return (
    <>
      {isInitialized ? (
        <QueryClientProvider client={new QueryClient()}>
          <RequestsCard
            employeeId={employeeInfo.employeeId}
            type={isRequestDateInPast ? RequestsCardType.PAST : RequestsCardType.UPCOMING}
            emptyCardText={
              isRequestDateInPast
                ? RequestsCardNoRequestsText.NO_PAST_REQUESTS
                : RequestsCardNoRequestsText.NO_UPCOMING_REQUESTS
            }
            dateRange={torDateRange}
            timeOffRequestOnClick={redirectViewRequest}
            countryCode={employeeInfo.countryCode}
            noOfRequestsToDisplay={NO_OF_REQUESTS_TO_DISPLAY}
            displayViewAllButton={true}
            viewAllRequestsOnClick={redirectViewAllRequests}
            displayViewMoreButton={false}
          />
        </QueryClientProvider>
      ) : (
        <Spinner size={SpinnerSize.Medium} />
      )}
    </>
  );
};

export default TimeoffRequestsView;
